import React from "react";
import { useHumanInTheLoop } from "@copilotkit/react-core/v2";
import { z } from "zod";
import { AskUserForm } from "./ask-user/ask-user-form";
import { AskUserArgs } from "./ask-user/build-a2ui-form";
import { HitlSummary } from "./hitl-summary";

const fieldSchema = z.object({
    name: z.string().describe("Key the answer is returned under (e.g. 'host')."),
    label: z.string().describe("Human-readable label shown to the user."),
    type: z
        .enum(["text", "longText", "number", "date", "boolean", "choice"])
        .default("text")
        .describe("Input type. Use 'choice' with `options` for a selection, 'boolean' for a checkbox."),
    options: z
        .array(z.string())
        .optional()
        .describe("Allowed values for a 'choice' field."),
    multiple: z
        .boolean()
        .optional()
        .describe("For 'choice' fields, allow selecting more than one option."),
    required: z
        .boolean()
        .optional()
        .describe("Whether the user must provide a value before submitting."),
    placeholder: z
        .string()
        .optional()
        .describe("Hint text appended to the label."),
    defaultValue: z
        .union([z.string(), z.number(), z.boolean()])
        .optional()
        .describe("Pre-filled value."),
    validationRegexp: z
        .string()
        .optional()
        .describe("Regex the entered text must match (text fields only).")
});

const parameters = z.object({
    title: z
        .string()
        .optional()
        .describe("Heading shown above the form."),
    description: z
        .string()
        .optional()
        .describe("Optional explanatory text shown below the title."),
    submitLabel: z
        .string()
        .optional()
        .describe("Label for the submit button (default 'Submit')."),
    cancelLabel: z
        .string()
        .optional()
        .describe("Label for the cancel button (default 'Cancel')."),
    fields: z
        .array(fieldSchema)
        .min(1)
        .describe("The fields to collect from the user.")
});

/**
 * Generic, catch-all human-in-the-loop tool. The agent calls `askUser` to collect arbitrary input
 * when no more specific HIL tool fits, defining the fields it needs at runtime. The form is
 * rendered with StriimFormBuilder and the collected values are returned to the agent.
 */
export const useAskUserTool = () => {
    useHumanInTheLoop({
        name: "askUser",
        description:
            "Generic form to collect input from the user. Use this ONLY when no more specific tool " +
            "(e.g. configureSource, configureTarget, checkAppNameAvailability, askInitialLoadOrCDC) fits the " +
            "use case. You define the fields you need at runtime via the `fields` array. Returns a JSON string: " +
            '{ "values": { <fieldName>: <answer> } } on submit, or { "cancelled": true } if the user cancels.',
        parameters,
        render: ({
            args,
            respond,
            status,
            name,
            result
        }: {
            args: AskUserArgs;
            respond: (r: string) => void;
            status: string;
            name: string;
            result?: string;
        }) => {
            if (status === "complete") return <HitlSummary toolName={name} result={result ?? ""} title={args.title} />;
            if (status !== "executing") return <></>;
            return <AskUserForm args={args} respond={respond} />;
        }
    });
};
